// Money boundary helpers. The API speaks integer minor units (paise/cents)
// and integer basis points (1bps = 0.01 percentage point) — see
// internal/models/quote.go's doc comment. NEVER do float arithmetic on a
// money value in this app: parse user input straight to an integer here at
// the boundary, and only ever divide-for-display (a single /100 + toFixed
// for showing a number, never fed back into a calculation) is safe.

// toFixedPointInt parses a decimal string (e.g. "1234.5", "-3", "") into an
// integer with 2 implied decimal places (1234.5 -> 123450), using string
// digit-shifting rather than `parseFloat(x) * 100` so there's no
// binary-float rounding drift (e.g. 19.99 * 100 !== 1999 in IEEE 754).
// Used for both money (minor units) and percentages (basis points) — both
// are "decimal value * 100, stored as an int" on the wire.
export function toFixedPointInt(input: string): number {
  const trimmed = (input ?? '').trim();
  if (trimmed === '' || trimmed === '-') return 0;
  const neg = trimmed.startsWith('-');
  const unsigned = neg ? trimmed.slice(1) : trimmed;
  const [intPartRaw, fracPartRaw = ''] = unsigned.split('.');
  const intPart = (intPartRaw || '0').replace(/[^0-9]/g, '') || '0';
  const fracPart = (fracPartRaw.replace(/[^0-9]/g, '') + '00').slice(0, 2);
  const n = parseInt(intPart + fracPart, 10);
  if (!Number.isFinite(n)) return 0;
  return neg ? -n : n;
}

export const parseMoneyToMinor = toFixedPointInt;
export const parsePercentToBps = toFixedPointInt;

// clamp restricts a number to [min, max] — used at the input boundary so an
// out-of-range value (e.g. a negative percent) is corrected before it's
// persisted, rather than saved as-is or silently dropped.
export function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

// Integer minor units -> a display string for populating a bindable input
// value (e.g. 123450 -> "1234.50"). Single divide for display only — never
// re-parsed back through float arithmetic, only through toFixedPointInt.
export function minorToInputValue(minor: number): string {
  return ((minor ?? 0) / 100).toFixed(2);
}

export function bpsToInputValue(bps: number): string {
  return ((bps ?? 0) / 100).toFixed(2);
}

const LOCALE_BY_CURRENCY: Record<string, string> = {
  INR: 'en-IN',
  USD: 'en-US',
  AED: 'en-AE',
  EUR: 'en-IE'
};

// formatMoney renders integer minor units as a localized currency string
// for display only.
export function formatMoney(minor: number, currency: string): string {
  const major = (minor ?? 0) / 100;
  try {
    return new Intl.NumberFormat(LOCALE_BY_CURRENCY[currency] ?? 'en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(major);
  } catch {
    return `${currency} ${major.toFixed(2)}`;
  }
}

export function formatPercent(bps: number): string {
  return `${((bps ?? 0) / 100).toFixed(2)}%`;
}

// ponytail: one runnable self-check for the money-parsing branch logic
// (string digit-shifting, sign handling) instead of a full test file/runner
// — this module has no other consumer of correctness guarantees at build
// time. Runs once in dev only; no-op in production builds.
function selfCheck() {
  const cases: [string, number][] = [
    ['0', 0],
    ['', 0],
    ['1234.5', 123450],
    ['1234.56', 123456],
    ['1234.567', 123456],
    ['-3', -300],
    ['-3.4', -340],
    ['19.99', 1999],
    ['100', 10000]
  ];
  for (const [input, expected] of cases) {
    const got = toFixedPointInt(input);
    console.assert(got === expected, `toFixedPointInt(${JSON.stringify(input)}) = ${got}, want ${expected}`);
  }
  console.assert(minorToInputValue(123450) === '1234.50', 'minorToInputValue round-trip');
}
if (import.meta.env.DEV) selfCheck();
